import { useMemo, useCallback } from 'react';
import { Upload, message } from 'antd';
import type { UploadProps, UploadFile } from 'antd';
import { useTranslation } from 'react-i18next';
import { storage } from '@/shared/lib/utils';
import { STORAGE_KEYS, env } from '@/shared/config';
import { deleteFile } from '@/entities/file/api/api';
import type { UseFileUploaderOptions } from './types';

const BASE_URL = env.apiBaseUrl;

const DEFAULT_MAX_COUNT = 1;
const DEFAULT_MAX_SIZE_MB = 5;
const DEFAULT_MIN_SIZE_MB = 0;

export const useFileUploader = (options: UseFileUploaderOptions) => {
  const {
    value,
    onChange,
    onRemove,
    accept,
    multiple,
    maxCount,
    disabled,
    minSizeMB,
    maxSizeMB,
    type,
    language,
    listType,
    showUploadList = true,
  } = options;

  const { t } = useTranslation();

  // Size calculations
  const effectiveMaxCount = maxCount ?? DEFAULT_MAX_COUNT;
  const effectiveMinSize = useMemo(() => (minSizeMB ?? DEFAULT_MIN_SIZE_MB) * 1024 * 1024, [minSizeMB]);
  const effectiveMaxSize = useMemo(() => (maxSizeMB ?? DEFAULT_MAX_SIZE_MB) * 1024 * 1024, [maxSizeMB]);
  const minSizeMBText = useMemo(() => (effectiveMinSize / (1024 * 1024)).toFixed(1), [effectiveMinSize]);
  const maxSizeMBText = useMemo(() => (effectiveMaxSize / (1024 * 1024)).toFixed(1), [effectiveMaxSize]);

  // Limit check
  const isLimitReached = useMemo(
    () => !!effectiveMaxCount && (value?.length ?? 0) >= effectiveMaxCount,
    [effectiveMaxCount, value?.length]
  );

  // Get auth token and stored language for header
  const token = useMemo(() => storage.get(STORAGE_KEYS.ACCESS_TOKEN), []);
  const storedLanguage = useMemo(() => storage.get(STORAGE_KEYS.LANGUAGE) || 'ru', []);

  // Change handler - process response and set image URL for preview
  const handleChange: UploadProps['onChange'] = useCallback(
    (info: Parameters<NonNullable<UploadProps['onChange']>>[0]) => {
      const processedFileList = info.fileList.map((file) => {
        // If upload is successful, extract data from response
        if (file.status === 'done' && file.response) {
          const result = file.response.result || file.response;
          if (result) {
            // Extract path, id, and name from response
            const fileId = result.id;
            const filePath = result.path || result.url;
            const fileName = result.name || file.name;

            // Build updated file object with response data
            return {
              ...file,
              // Only update uid if it's not already set (preserve existing uids for loaded files)
              uid: file.uid || (fileId ? `${fileId}` : file.uid),
              name: fileName,
              // Set URL for image preview - prioritize path from response, fallback to existing url
              url: filePath || file.url,
              response: {
                ...file.response,
                result: {
                  id: fileId,
                  path: filePath,
                  name: fileName,
                },
              },
            };
          }
        }
        // For files that are uploading, already loaded, or have errors, return as is
        // but ensure URL is preserved if it exists
        return {
          ...file,
          // Preserve existing URL if file already has one (for pre-loaded files)
          url: file.url || (file.thumbUrl ? undefined : file.url),
        };
      });

      onChange?.(processedFileList);
    },
    [onChange]
  );

  // Before upload validation
  const beforeUpload = useCallback(
    (file: File) => {
      // Check limit before upload (but allow removing existing files)
      if (isLimitReached) {
        message.warning(`Maximum ${effectiveMaxCount} file(s) allowed. Please remove existing file first.`);
        return Upload.LIST_IGNORE;
      }

      const size = file.size ?? 0;

      if (size && effectiveMinSize > 0 && size < effectiveMinSize) {
        message.error(t('fileUploader.errors.tooSmall', { min: minSizeMBText }));
        return Upload.LIST_IGNORE;
      }

      if (size && effectiveMaxSize > 0 && size > effectiveMaxSize) {
        message.error(t('fileUploader.errors.tooLarge', { max: maxSizeMBText }));
        return Upload.LIST_IGNORE;
      }

      return true;
    },
    [effectiveMinSize, effectiveMaxSize, minSizeMBText, maxSizeMBText, t, isLimitReached, effectiveMaxCount]
  );

  // Handle file remove.
  // Only newly-uploaded files (uploaded in the current session but not yet
  // linked to a saved record) are deleted from the backend immediately, since
  // otherwise they would become orphans. Files loaded from the server must NOT
  // be deleted here — deleting them would permanently remove the image even if
  // the user leaves the form without saving. For those, removal only updates
  // the form state and the change is persisted when the form is submitted.
  const handleRemove = useCallback(
    async (file: UploadFile) => {
      const isNewlyUploaded = Boolean(file.originFileObj);

      if (isNewlyUploaded) {
        try {
          const fileId = file.response?.result?.id ?? file.response?.id;
          if (fileId) {
            await deleteFile(fileId);
          }
        } catch (error) {
          console.error('Error deleting file:', error);
          message.error('Failed to delete file');
          return false;
        }
      }

      // Call custom onRemove if provided
      if (onRemove) {
        return await onRemove(file);
      }

      return true;
    },
    [onRemove]
  );

  // Determine Ant Design listType based on listType prop
  const antListType = useMemo(() => {
    if (listType === 'block') return 'picture-card'; // Show images as cards
    if (listType === 'list') return 'text'; // Show only names
    return 'picture-card'; // Default to picture-card for backward compatibility
  }, [listType]);

  // Upload props configuration
  const uploadProps: UploadProps = useMemo(() => {
    // Build data object: include type and/or language only if provided
    const data: Record<string, string> = {};
    if (type) data.type = type;
    if (language) data.language = language;

    return {
      multiple,
      accept,
      maxCount: effectiveMaxCount,
      disabled: disabled, // Only disable if explicitly disabled, not when limit is reached
      fileList: value,
      action: `${BASE_URL}/v1/admin/file/create`,
      name: 'attachment',
      data: Object.keys(data).length > 0 ? data : undefined,
      headers: {
        Authorization: `Bearer ${token}`,
        'Accept-Language': storedLanguage,
      },
      beforeUpload,
      onChange: handleChange,
      onRemove: handleRemove,
      showUploadList: showUploadList
        ? {
            showRemoveIcon: !disabled, // Allow removing files even when limit is reached
            showPreviewIcon: antListType === 'picture-card', // Show preview for picture-card mode
            showDownloadIcon: false,
          }
        : false,
      listType: antListType,
    };
  }, [
    multiple,
    accept,
    effectiveMaxCount,
    disabled,
    value,
    token,
    storedLanguage,
    beforeUpload,
    handleChange,
    handleRemove,
    type,
    language,
    antListType,
    showUploadList,
  ]);

  return {
    uploadProps,
    isLimitReached,
    maxSizeMBText,
    minSizeMBText,
  };
};
